import { useState } from "react";
import { FREE_TIER_LIMITS, FEATURE_NAMES, type UsageFeature } from "../lib/userStats";
import { createCheckoutSession } from "../lib/subscription";
import PromoCodeInput from "./PromoCodeInput";

interface UsageLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  feature: UsageFeature;
  currentUsage: number;
  isPremiumOnly: boolean;
}

export default function UsageLimitModal({ isOpen, onClose, feature, currentUsage, isPremiumOnly }: UsageLimitModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPromoInput, setShowPromoInput] = useState(false);

  if (!isOpen) return null;

  const limit = FREE_TIER_LIMITS[feature];
  const featureName = FEATURE_NAMES[feature];

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-bg rounded-md max-w-md w-full overflow-hidden border border-border">
        {/* Header */}
        <div className="px-6 py-6 border-b border-border-light">
          <h2 className="text-lg font-semibold text-text">
            {isPremiumOnly ? "Premium Feature" : "Monthly Limit Reached"}
          </h2>
          <p className="text-sm text-muted mt-1">
            {isPremiumOnly
              ? `${featureName} is a Premium feature.`
              : `You've used all ${limit} free ${featureName.toLowerCase()} this month.`}
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {!isPremiumOnly && (
            <div className="mb-6">
              <div className="flex justify-between text-sm font-[family-name:--font-ui] mb-2">
                <span className="text-muted">Monthly usage</span>
                <span className="text-text font-medium">{currentUsage} / {limit}</span>
              </div>
              <div className="h-1.5 bg-border rounded-full overflow-hidden">
                <div
                  className="h-full bg-gi-high rounded-full"
                  style={{ width: "100%" }}
                />
              </div>
            </div>
          )}

          <div className="space-y-2 mb-6">
            <p className="text-sm text-text font-[family-name:--font-ui] font-medium">Premium includes:</p>
            <ul className="text-sm text-muted font-[family-name:--font-ui] space-y-1">
              <li>- Unlimited chat questions</li>
              <li>- Unlimited meal builder and planner</li>
              <li>- Menu decoder and grocery optimizer</li>
              <li>- Priority AI responses</li>
            </ul>
          </div>

          {error && (
            <div className="mb-4 p-3 border border-gi-high/20 bg-gi-high-bg rounded-md">
              <p className="text-sm text-gi-high font-[family-name:--font-ui]">{error}</p>
            </div>
          )}

          <button
            onClick={async () => {
              setIsLoading(true);
              setError(null);
              try {
                const { url } = await createCheckoutSession(
                  `${window.location.origin}/profile?checkout=success`,
                  `${window.location.origin}/profile?checkout=cancelled`
                );
                window.location.href = url;
              } catch (err) {
                console.error("Checkout error:", err);
                setError("Unable to start checkout. Please try again.");
                setIsLoading(false);
              }
            }}
            disabled={isLoading}
            className="w-full py-2.5 bg-accent text-white rounded-md font-[family-name:--font-ui] text-sm font-medium hover:opacity-85 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-opacity"
          >
            {isLoading ? (
              <>
                <div className="spinner spinner--sm" style={{ width: 16, height: 16, borderWidth: 2 }} />
                Processing...
              </>
            ) : (
              "Upgrade to Premium — $4.99/mo"
            )}
          </button>

          {/* Promo Code Section */}
          <div className="mt-4 pt-4 border-t border-border-light">
            {showPromoInput ? (
              <PromoCodeInput
                compact
                onSuccess={() => {
                  onClose();
                  window.location.reload();
                }}
              />
            ) : (
              <button
                onClick={() => setShowPromoInput(true)}
                className="w-full text-sm text-accent font-[family-name:--font-ui] font-medium hover:underline"
              >
                Have a promo code?
              </button>
            )}
          </div>

          <button
            onClick={onClose}
            className="w-full mt-3 py-2.5 border border-border rounded-md text-text font-[family-name:--font-ui] text-sm font-medium hover:bg-black/5 transition-colors"
          >
            Maybe Later
          </button>

          <p className="text-[11px] text-muted text-center mt-4 font-[family-name:--font-ui]">
            Limits reset on the 1st of each month
          </p>
        </div>
      </div>
    </div>
  );
}
